import {Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BokkzWageDataService,
  Component1,
  Component2,
  Dienstverband,
  LoonstaatEntity,
  Periode,
  VerloningsPeriode,
  WerknemerEntity,
  beginDatumNietVoorDatumInDienstValidator,
  eindDatumNietVoorBeginDatumValidator,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {debounceTime, filter, merge, Observable, shareReplay, Subscription, take, tap} from 'rxjs';
import {SharedUiAlertModule} from "@snelstart/ui-alert";
import {right} from "@popperjs/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type ComponentFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  component: FormControl<Component2 | null>;
  displayValue: FormControl<string | null>;
  eenheid: FormControl<number | null>;
  bedrag: FormControl<number | null>;
  percentage: FormControl<number | null>;
  proRataVergoeding: FormControl<number | null>;
  grondslagComponenten: FormControl<number[] | null>;
  periodeVrijval: FormControl<number | null>;
  cumulatiefSaldoBeginPeriode: FormControl<number | null>;
  berekendeAanwas: FormControl<number | null>;
  cumulatiefSaldoEindPeriode: FormControl<number | null>;
}>;

type LijstFormGroup = FormGroup<{
  variabeleVergoedingen: FormArray<ComponentFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-variabelevergoeding',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule,
    SharedUiAlertModule
  ],
  templateUrl: './variabelevergoeding.component.html',
  styleUrl: './variabelevergoeding.component.scss'
})
export class VariabelevergoedingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected readonly right = right;
  public dialogVisible = false;
  public dialogVisibleChange = new EventEmitter<boolean>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzWageDataService);

  private werknemerEntity = {} as WerknemerEntity;
  private loonstaatEntity = {} as LoonstaatEntity;

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: false,
  });

  public lijstFg: LijstFormGroup = new FormGroup({
    variabeleVergoedingen: new FormArray<ComponentFormGroup>([]),
  });

  public periode?: Periode;
  public startDatePeriode: any;
  public endDatePeriode: any;
  public index = 0;
  public selectedPeriode = 0;
  private bedragChangeSubscription?: Subscription;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public optionsComponenten: SelectFormControlItem<Component2>[] = [
    {value: Component2.REISKOSTEN_WOON_WERK_VARIABEL, label: 'Netto reiskosten woon-werk'},
    {value: Component2.REISKOSTEN_ZAKELIJK, label: "Netto reiskosten zakelijke km's"},
    {value: Component2.BONUS, label: 'Bonus'},
    {value: Component2.DIVERS_BRUTO_VERGOEDING_1, label: "Vrije keuze omschr. 1 (tab.tar.)"},
    {value: Component2.DIVERS_BRUTO_VERGOEDING_2, label: "Vrije keuze omschr. 2 (tab.tar.)"},
    {value: Component2.DIVERS_BRUTO_VERGOEDING_3, label: "Vrije keuze omschr. 3 (tab.tar.)"},
    {value: Component2.DIVERS_NETTO_VERGOEDING_1, label: "Vrije keuze omschr. 1 (netto)"},
    {value: Component2.DIVERS_NETTO_VERGOEDING_2, label: "Vrije keuze omschr. 2 (netto)"},
    {value: Component2.DIVERS_NETTO_VERGOEDING_3, label: "Vrije keuze omschr. 3 (netto)"},
  ];

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly loonstaatEntity$: Observable<LoonstaatEntity> =
    this.dataService.loonstaatEntity$.pipe(
      filter((data): data is LoonstaatEntity => !!data?.id),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedPeriode$: Observable<number> =
    this.dataService.selectedPeriode$.pipe(
      filter((data): data is number => !!data),
      tap(data => {
        this.selectedPeriode = data;
        this.syncForms();
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.werknemerEntity$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.werknemerEntity = data;
      this.syncForms();
    });
    this.loonstaatEntity$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.loonstaatEntity = data;
      this.syncForms();
    });
    this.selectedPeriode$.subscribe();
    this.bindBedragChangeHandlers();
  }

  ngOnDestroy(): void {
    this.bedragChangeSubscription?.unsubscribe();
    this.saveForm();
  }

  showDialog() {
    this.dialogVisible = true;
  }

  private syncForms(): void {
    if (!this.werknemerEntity?.werknemerGegevens) {
      return;
    }

    const verloningsPeriode = this.getCurrentVerloningsPeriode();
    this.applyPeriodeContext(verloningsPeriode);
    this.updateForm();
  }

  private getCurrentVerloningsPeriode(): VerloningsPeriode | undefined {
    const periodes = this.loonstaatEntity.loonStaat?.verloningsPeriode ?? [];
    return periodes.find((periode) => periode.periodenummer === this.selectedPeriode)
      ?? periodes.at(this.selectedPeriode - 1);
  }

  private applyPeriodeContext(verloningsPeriode?: VerloningsPeriode): void {
    this.periode = verloningsPeriode?.periode;
    this.startDatePeriode = this.periode?.begindatum;
    this.endDatePeriode = this.periode?.einddatum;
  }

  get getVariabeleVergoedingen(): FormArray<ComponentFormGroup> {
    return this.lijstFg.controls.variabeleVergoedingen;
  }

  addRij(): void {
    this.getVariabeleVergoedingen.push(this.createFormGroup(true, undefined));
    this.bindBedragChangeHandlers();
  }

  verwijderRij(index: number): void {
    this.getVariabeleVergoedingen.removeAt(index, { emitEvent: false });
    this.getVariabeleVergoedingen.updateValueAndValidity({ emitEvent: false });
    this.bindBedragChangeHandlers();
    this.saveForm();
    this.dataService
      .calculateLatestWerknemerEntity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private bindBedragChangeHandlers(): void {
    this.bedragChangeSubscription?.unsubscribe();

    const bedragStreams = this.getVariabeleVergoedingen.controls.map(control =>
      control.controls.bedrag.valueChanges
    );

    if (!bedragStreams.length) {
      this.bedragChangeSubscription = undefined;
      return;
    }

    this.bedragChangeSubscription = merge(...bedragStreams)
      .pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveForm();
        this.dataService
          .calculateLatestWerknemerEntity()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  public showInPeriode(item: { periode?: { begindatum?: Date | null; einddatum?: Date | null } }) {
    const dateStart = item.periode?.begindatum;
    const dateEnd = item.periode?.einddatum;

    if (!dateStart || !dateEnd || !this.startDatePeriode || !this.endDatePeriode) {
      return true;
    }

    const startDatePeriode = new Date(this.startDatePeriode);
    const endDatePeriode = new Date(this.endDatePeriode);

    return (
      new Date(dateStart).getTime() === startDatePeriode.getTime() &&
      new Date(dateEnd).getTime() === endDatePeriode.getTime()
    );
  }

  private getDatumInDienst(): Date | string | null | undefined {
    return this.werknemerEntity?.werknemerGegevens?.dienstverbanden?.[0]?.datumInDienst;
  }

  public heeftPeriodeValidationErrors(): boolean {
    return this.getVariabeleVergoedingen.controls.some(control =>
      control.controls.periode.hasError('einddatumVoorBegindatum') ||
      control.controls.periode.hasError('begindatumVoorDatumInDienst')
    );
  }

  public heeftBegindatumVoorDatumInDienstErrors(): boolean {
    return this.getVariabeleVergoedingen.controls.some(control =>
      control.controls.periode.hasError('begindatumVoorDatumInDienst')
    );
  }

  createFormGroup(isVar: boolean, item?: Component1): ComponentFormGroup {
    return new FormGroup({
      periode: new FormGroup({
        begindatum: new FormControl<Date | null>(
          isVar ? (this.startDatePeriode ?? null) : item?.periode?.begindatum ?? null
        ),
        einddatum: new FormControl<Date | null>(
          isVar ? (this.endDatePeriode ?? null) : item?.periode?.einddatum ?? null
        ),
      }, {
        validators: [
          eindDatumNietVoorBeginDatumValidator(),
          beginDatumNietVoorDatumInDienstValidator(() => this.getDatumInDienst()),
        ]
      }),
      component: new FormControl<Component2 | null>(item?.component ?? null),
      displayValue: new FormControl<string | null>(item?.displayValue ?? null),
      eenheid: new FormControl<number | null>(item?.eenheid ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      percentage: new FormControl<number | null>(item?.percentage ?? null),
      proRataVergoeding: new FormControl<number | null>(item?.proRataVergoeding ?? null),
      grondslagComponenten: new FormControl<number[] | null>(item?.grondslagComponenten ?? null),
      periodeVrijval: new FormControl<number | null>(item?.periodeVrijval ?? null),
      cumulatiefSaldoBeginPeriode: new FormControl<number | null>(item?.cumulatiefSaldoBeginPeriode ?? null),
      berekendeAanwas: new FormControl<number | null>(item?.berekendeAanwas ?? null),
      cumulatiefSaldoEindPeriode: new FormControl<number | null>(item?.cumulatiefSaldoEindPeriode ?? null),
    });
  }

  private updateForm(): void {
    const lijst = this.werknemerEntity.werknemerGegevens?.dienstverbanden?.at(0)?.variabeleVergoedingen || [];
    const formArray = this.getVariabeleVergoedingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(true, lijst[i])
        formArray.push(formGroup, {emitEvent: false});
      }
    }

    this.bindBedragChangeHandlers();
  }

  public saveForm(): void {
    this.lijstFg.updateValueAndValidity({emitEvent: false});

    if (this.heeftPeriodeValidationErrors()) {
      return;
    }

    const variabeleVergoedingen = this.lijstFg.getRawValue().variabeleVergoedingen;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        variabeleVergoedingen,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband],
        },
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }
}
